import React, { useEffect, useState, useMemo } from 'react';
import { Card, CardHeader, Table, Btn, Badge, Modal, FormRow, FormField, Spinner } from '../components/ui/Card';
import api from '../utils/api';
import toast from 'react-hot-toast';
import { useAuth } from '../contexts/AuthContext';

const TYPES = ['Congé annuel','Congé maladie','Congé maternité/paternité','Congé sans solde','Permission spéciale'];
const QUOTA = 30;
const QUOTA_PERMISSION = 10;
const TODAY = new Date().toISOString().split('T')[0];

// Évite le décalage UTC : '2024-01-15' → date locale correcte
const fmtDate = d => {
  if (!d) return '—';
  const s = String(d).slice(0, 10);
  const [y, m, day] = s.split('-').map(Number);
  return new Date(y, m - 1, day).toLocaleDateString('fr-FR');
};

function joursOuvrables(d1, d2) {
  if (!d1||!d2) return 0;
  const [y1,m1,j1]=d1.slice(0,10).split('-').map(Number);
  const [y2,m2,j2]=d2.slice(0,10).split('-').map(Number);
  let count=0, cur=new Date(y1,m1-1,j1), end=new Date(y2,m2-1,j2);
  if (end<cur) return 0;
  while(cur<=end){ if(cur.getDay()!==0&&cur.getDay()!==6) count++; cur.setDate(cur.getDate()+1); }
  return count;
}

export default function AbsencesPage() {
  const { hasRole } = useAuth();
  const canApprove = hasRole('super_admin','admin');
  const canDelete  = hasRole('super_admin','admin','rh');
  const [absences,  setAbsences]  = useState([]);
  const [personnel, setPersonnel] = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [form, setForm] = useState({ personnel_id:'', type:'Congé annuel', start_date:TODAY, end_date:'', reason:'' });
  const [saving, setSaving] = useState(false);
  const [filterStatut, setFilterStatut] = useState('');

  const load = async (signal) => {
    setLoading(true);
    try {
      const [a,p] = await Promise.all([api.get('/absences'),api.get('/personnel')]);
      if (signal?.aborted) return;
      setAbsences(a.data.data||[]);
      setPersonnel(p.data.data||[]);
    } catch { if (!signal?.aborted) toast.error('Erreur de chargement'); }
    finally { if (!signal?.aborted) setLoading(false); }
  };

  useEffect(()=>{
    document.getElementById('page-title')&&(document.getElementById('page-title').textContent='Absences & Congés');
    const ctrl = new AbortController();
    load(ctrl.signal);
    return () => ctrl.abort();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  },[]);

  const congesParPersonnel = useMemo(()=>{
    const map={};
    absences.forEach(a=>{
      if(a.type==='Congé annuel'&&a.status==='approved'){
        const pid=a.personnel_id;
        if(!map[pid]) map[pid]=0;
        map[pid]+=joursOuvrables(a.start_date,a.end_date);
      }
    });
    return map;
  },[absences]);

  const permissionsParPersonnel = useMemo(()=>{
    const map={};
    absences.forEach(a=>{
      if(a.type==='Permission spéciale'&&a.status==='approved'){
        const pid=a.personnel_id;
        if(!map[pid]) map[pid]=0;
        map[pid]+=joursOuvrables(a.start_date,a.end_date);
      }
    });
    return map;
  },[absences]);

  const joursForm = useMemo(()=>joursOuvrables(form.start_date,form.end_date),[form.start_date,form.end_date]);

  const resteSelectionne = useMemo(()=>{
    if(!form.personnel_id||form.type!=='Congé annuel') return null;
    return QUOTA-(congesParPersonnel[form.personnel_id]||0);
  },[form.personnel_id,form.type,congesParPersonnel]);
  const quotaDepasse = resteSelectionne!==null && joursForm>resteSelectionne;

  const restePermission = useMemo(()=>{
    if(!form.personnel_id||form.type!=='Permission spéciale') return null;
    return QUOTA_PERMISSION-(permissionsParPersonnel[form.personnel_id]||0);
  },[form.personnel_id,form.type,permissionsParPersonnel]);
  const quotaPermissionDepasse = restePermission!==null && joursForm>restePermission;
  const permissionAtteinte = restePermission!==null && restePermission<=0;

  const filtered = filterStatut ? absences.filter(a=>a.status===filterStatut) : absences;

  const handleCreate = async e => {
    e.preventDefault();
    if(quotaDepasse){ toast.error(`Quota dépassé ! Il reste ${resteSelectionne} jour(s).`); return; }
    if(permissionAtteinte){ toast.error('Quota de permissions spéciales atteint (10 jours/an).'); return; }
    if(quotaPermissionDepasse){ toast.error(`Quota de permissions dépassé ! Il reste ${restePermission} jour(s).`); return; }
    setSaving(true);
    try {
      await api.post('/absences',{ personnel_id:form.personnel_id, type:form.type, start_date:form.start_date, end_date:form.end_date, reason:form.reason });
      toast.success('Demande créée'); setModalOpen(false); load();
    } catch(err){ toast.error(err.response?.data?.message||'Erreur'); }
    finally{ setSaving(false); }
  };

  const handleValidate = async (id, status) => {
    try{ await api.put(`/absences/${id}`,{status}); toast.success(`Demande ${status}`); load(); }
    catch{ toast.error('Erreur'); }
  };

  const handleDelete = async id => {
    if (!window.confirm('Supprimer définitivement cette demande ?')) return;
    try{ await api.delete(`/absences/${id}`); toast.success('Demande supprimée'); load(); }
    catch{ toast.error('Erreur lors de la suppression'); }
  };

  const stats = { total:absences.length, pending:absences.filter(a=>a.status==='pending').length, approved:absences.filter(a=>a.status==='approved').length, rejected:absences.filter(a=>a.status==='rejected').length };

  const badge = s => {
    const m = {pending:'warning',approved:'success',rejected:'error'};
    return <Badge type={m[s]||'default'}>{s==='pending'?'En attente':s==='approved'?'Approuvé':'Refusé'}</Badge>;
  };

  const columns = [
    { key:'first_name', header:'Personnel', render:(_,row)=>{
      const pid=row.personnel_id;
      const pris=congesParPersonnel[pid]||0;
      const prisP=permissionsParPersonnel[pid]||0;
      const isAnnuel=row.type==='Congé annuel';
      const isPermission=row.type==='Permission spéciale';
      return(
        <div>
          <p style={{fontWeight:600,color:'var(--navy)'}}>{row.first_name} {row.last_name}</p>
          {isAnnuel&&<p style={{fontSize:'0.72rem',color:pris>=QUOTA?'#dc2626':'#16a34a',marginTop:2}}>{QUOTA-pris} j. restants / {QUOTA}</p>}
          {isPermission&&<p style={{fontSize:'0.72rem',color:prisP>=QUOTA_PERMISSION?'#dc2626':'#7c3aed',marginTop:2}}>{QUOTA_PERMISSION-prisP} j. restants / {QUOTA_PERMISSION}</p>}
        </div>
      );
    }},
    { key:'type', header:'Type', render:v=><Badge type={v==='Congé annuel'?'gold':'info'}>{v}</Badge> },
    { key:'start_date', header:'Début', render:v=>fmtDate(v) },
    { key:'end_date',   header:'Fin',   render:v=>fmtDate(v) },
    { key:'start_date', header:'Durée', render:(_,row)=><span style={{fontWeight:700}}>{joursOuvrables(row.start_date,row.end_date)} j.</span> },
    { key:'reason', header:'Motif', render:v=><span style={{fontSize:'0.82rem',color:'var(--gray-500)'}}>{v||'—'}</span> },
    { key:'status', header:'Statut', render:v=>badge(v) },
    { key:'actions', header:'Actions', render:(_,row)=>(
      <div style={{display:'flex',gap:5}}>
        {canApprove&&row.status==='pending'&&<>
          <Btn size="sm" variant="success" onClick={()=>handleValidate(row.id,'approved')}>✅</Btn>
          <Btn size="sm" variant="danger"  onClick={()=>handleValidate(row.id,'rejected')}>❌</Btn>
        </>}
        {canDelete&&<Btn size="sm" variant="danger" onClick={()=>handleDelete(row.id)} title="Supprimer la demande">🗑️</Btn>}
      </div>
    )},
  ];

  return(
    <div className="animate-fade">
      <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(130px,1fr))',gap:12,marginBottom:18}}>
        {[
          {label:'Total',      value:stats.total,    color:'var(--navy)',    icon:'📋'},
          {label:'En attente', value:stats.pending,  color:'#f59e0b',       icon:'⏳'},
          {label:'Approuvés',  value:stats.approved, color:'var(--emerald)',icon:'✅'},
          {label:'Refusés',    value:stats.rejected, color:'var(--red)',    icon:'❌'},
        ].map(s=>(
          <div key={s.label} style={{background:'white',borderRadius:'var(--radius)',boxShadow:'var(--shadow)',padding:'14px 18px',borderLeft:`4px solid ${s.color}`}}>
            <p style={{fontSize:'0.72rem',color:'var(--gray-400)',marginBottom:4}}>{s.icon} {s.label}</p>
            <p style={{fontSize:'1.8rem',fontWeight:700,color:s.color,lineHeight:1}}>{s.value}</p>
          </div>
        ))}
      </div>

      {/* Suivi des congés annuels */}
      <Card style={{marginBottom:16}}>
        <CardHeader title="Suivi des congés annuels" subtitle={`Quota : ${QUOTA} jours/an — 5 dernières demandes`} />
        <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:16,alignItems:'start'}}>

          {/* 5 dernières demandes de congé annuel */}
          <div>
            <p style={{fontSize:'0.72rem',fontWeight:700,color:'var(--gray-500)',textTransform:'uppercase',letterSpacing:'0.05em',marginBottom:10}}>5 dernières demandes</p>
            {absences
              .filter(a=>a.type==='Congé annuel')
              .slice()
              .sort((a,b)=>new Date(b.created_at||b.start_date)-new Date(a.created_at||a.start_date))
              .slice(0,5)
              .map(a=>{
                const p=personnel.find(x=>x.id===a.personnel_id);
                const pris=congesParPersonnel[a.personnel_id]||0;
                const reste=Math.max(0,QUOTA-pris);
                const duree=joursOuvrables(a.start_date,a.end_date);
                const statusColor={pending:'#f59e0b',approved:'#16a34a',rejected:'#dc2626'}[a.status]||'#9ca3af';
                return(
                  <div key={a.id} style={{display:'flex',alignItems:'center',gap:10,padding:'9px 12px',borderRadius:9,border:'1px solid var(--gray-200)',marginBottom:7,background:'white'}}>
                    <div style={{width:34,height:34,borderRadius:'50%',overflow:'hidden',border:'1.5px solid var(--gray-200)',background:'var(--gray-100)',display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0}}>
                      {p?.photo?<img src={p.photo} alt="" style={{width:'100%',height:'100%',objectFit:'cover'}}/>:<span style={{fontSize:'0.85rem'}}>👤</span>}
                    </div>
                    <div style={{flex:1,minWidth:0}}>
                      <p style={{fontWeight:600,color:'var(--navy)',fontSize:'0.82rem',overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>
                        {a.first_name||p?.first_name} {a.last_name||p?.last_name}
                      </p>
                      <p style={{fontSize:'0.7rem',color:'var(--gray-400)'}}>{p?.poste||'—'}</p>
                    </div>
                    <div style={{textAlign:'right',flexShrink:0}}>
                      <p style={{fontSize:'0.78rem',fontWeight:700,color:'var(--navy)'}}>{duree} j. demandé{duree>1?'s':''}</p>
                      <p style={{fontSize:'0.68rem',color:reste<=5?'#dc2626':'#16a34a',fontWeight:600}}>{reste} j. restants</p>
                    </div>
                    <div style={{width:8,height:8,borderRadius:'50%',background:statusColor,flexShrink:0}} title={a.status}/>
                  </div>
                );
              })}
            {absences.filter(a=>a.type==='Congé annuel').length===0&&(
              <p style={{color:'var(--gray-400)',fontStyle:'italic',fontSize:'0.82rem',padding:'12px 4px'}}>Aucune demande de congé annuel</p>
            )}
          </div>

          {/* Quota par personnel actif */}
          <div>
            <p style={{fontSize:'0.72rem',fontWeight:700,color:'var(--gray-500)',textTransform:'uppercase',letterSpacing:'0.05em',marginBottom:10}}>Quota par membre</p>
            <div style={{display:'flex',flexDirection:'column',gap:7}}>
              {personnel.filter(p=>p.status==='active').map(p=>{
                const pris=congesParPersonnel[p.id]||0;
                const reste=Math.max(0,QUOTA-pris);
                const pct=Math.min(100,(pris/QUOTA)*100);
                const color=pct>=100?'#dc2626':pct>=80?'#f59e0b':'#16a34a';
                const tag=pct>=100?' ⛔':reste<=5?` (${reste}j)`:'';
                return(
                  <div key={p.id} style={{border:'1px solid var(--gray-200)',borderRadius:9,padding:'8px 12px',background:'white'}}>
                    <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:5}}>
                      <div style={{display:'flex',alignItems:'center',gap:7,minWidth:0}}>
                        <div style={{width:26,height:26,borderRadius:'50%',overflow:'hidden',border:'1.5px solid var(--gray-200)',background:'var(--gray-100)',display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0}}>
                          {p.photo?<img src={p.photo} alt="" style={{width:'100%',height:'100%',objectFit:'cover'}}/>:<span style={{fontSize:'0.75rem'}}>👤</span>}
                        </div>
                        <div style={{minWidth:0}}>
                          <p style={{fontWeight:600,color:'var(--navy)',fontSize:'0.8rem',overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{p.first_name} {p.last_name}{tag}</p>
                          <p style={{fontSize:'0.68rem',color:'var(--gray-400)'}}>{p.poste}</p>
                        </div>
                      </div>
                      <span style={{fontSize:'0.72rem',fontWeight:700,color,flexShrink:0,marginLeft:8}}>{reste}/{QUOTA} j.</span>
                    </div>
                    <div style={{height:5,background:'var(--gray-200)',borderRadius:99,overflow:'hidden'}}>
                      <div style={{height:'100%',width:`${pct}%`,background:color,borderRadius:99}} />
                    </div>
                  </div>
                );
              })}
              {personnel.filter(p=>p.status==='active').length===0&&(
                <p style={{color:'var(--gray-400)',fontStyle:'italic',fontSize:'0.82rem'}}>Aucun personnel actif</p>
              )}
            </div>
          </div>
        </div>
      </Card>

      {/* Suivi des permissions spéciales */}
      <Card style={{marginBottom:16}}>
        <CardHeader title="Suivi des permissions spéciales" subtitle={`Quota : ${QUOTA_PERMISSION} jours/an — 5 dernières demandes`} />
        <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:16,alignItems:'start'}}>

          {/* 5 dernières demandes de permission spéciale */}
          <div>
            <p style={{fontSize:'0.72rem',fontWeight:700,color:'var(--gray-500)',textTransform:'uppercase',letterSpacing:'0.05em',marginBottom:10}}>5 dernières demandes</p>
            {absences
              .filter(a=>a.type==='Permission spéciale')
              .slice()
              .sort((a,b)=>new Date(b.created_at||b.start_date)-new Date(a.created_at||a.start_date))
              .slice(0,5)
              .map(a=>{
                const p=personnel.find(x=>x.id===a.personnel_id);
                const pris=permissionsParPersonnel[a.personnel_id]||0;
                const reste=Math.max(0,QUOTA_PERMISSION-pris);
                const duree=joursOuvrables(a.start_date,a.end_date);
                const statusColor={pending:'#f59e0b',approved:'#16a34a',rejected:'#dc2626'}[a.status]||'#9ca3af';
                return(
                  <div key={a.id} style={{display:'flex',alignItems:'center',gap:10,padding:'9px 12px',borderRadius:9,border:'1px solid var(--gray-200)',marginBottom:7,background:'white'}}>
                    <div style={{width:34,height:34,borderRadius:'50%',overflow:'hidden',border:'1.5px solid var(--gray-200)',background:'var(--gray-100)',display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0}}>
                      {p?.photo?<img src={p.photo} alt="" style={{width:'100%',height:'100%',objectFit:'cover'}}/>:<span style={{fontSize:'0.85rem'}}>👤</span>}
                    </div>
                    <div style={{flex:1,minWidth:0}}>
                      <p style={{fontWeight:600,color:'var(--navy)',fontSize:'0.82rem',overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>
                        {a.first_name||p?.first_name} {a.last_name||p?.last_name}
                      </p>
                      <p style={{fontSize:'0.7rem',color:'var(--gray-400)'}}>{p?.poste||'—'}</p>
                    </div>
                    <div style={{textAlign:'right',flexShrink:0}}>
                      <p style={{fontSize:'0.78rem',fontWeight:700,color:'var(--navy)'}}>{duree} j. demandé{duree>1?'s':''}</p>
                      <p style={{fontSize:'0.68rem',color:reste<=2?'#dc2626':'#7c3aed',fontWeight:600}}>{reste} j. restants</p>
                    </div>
                    <div style={{width:8,height:8,borderRadius:'50%',background:statusColor,flexShrink:0}} title={a.status}/>
                  </div>
                );
              })}
            {absences.filter(a=>a.type==='Permission spéciale').length===0&&(
              <p style={{color:'var(--gray-400)',fontStyle:'italic',fontSize:'0.82rem',padding:'12px 4px'}}>Aucune demande de permission spéciale</p>
            )}
          </div>

          {/* Quota de permissions par personnel actif */}
          <div>
            <p style={{fontSize:'0.72rem',fontWeight:700,color:'var(--gray-500)',textTransform:'uppercase',letterSpacing:'0.05em',marginBottom:10}}>Quota par membre</p>
            <div style={{display:'flex',flexDirection:'column',gap:7}}>
              {personnel.filter(p=>p.status==='active').map(p=>{
                const pris=permissionsParPersonnel[p.id]||0;
                const reste=Math.max(0,QUOTA_PERMISSION-pris);
                const pct=Math.min(100,(pris/QUOTA_PERMISSION)*100);
                const color=pct>=100?'#dc2626':pct>=70?'#f59e0b':'#7c3aed';
                const tag=pct>=100?' ⛔':reste<=2?` (${reste}j)`:'';
                return(
                  <div key={p.id} style={{border:'1px solid var(--gray-200)',borderRadius:9,padding:'8px 12px',background:'white'}}>
                    <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:5}}>
                      <div style={{display:'flex',alignItems:'center',gap:7,minWidth:0}}>
                        <div style={{width:26,height:26,borderRadius:'50%',overflow:'hidden',border:'1.5px solid var(--gray-200)',background:'var(--gray-100)',display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0}}>
                          {p.photo?<img src={p.photo} alt="" style={{width:'100%',height:'100%',objectFit:'cover'}}/>:<span style={{fontSize:'0.75rem'}}>👤</span>}
                        </div>
                        <div style={{minWidth:0}}>
                          <p style={{fontWeight:600,color:'var(--navy)',fontSize:'0.8rem',overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{p.first_name} {p.last_name}{tag}</p>
                          <p style={{fontSize:'0.68rem',color:'var(--gray-400)'}}>{p.poste}</p>
                        </div>
                      </div>
                      <span style={{fontSize:'0.72rem',fontWeight:700,color,flexShrink:0,marginLeft:8}}>{reste}/{QUOTA_PERMISSION} j.</span>
                    </div>
                    <div style={{height:5,background:'var(--gray-200)',borderRadius:99,overflow:'hidden'}}>
                      <div style={{height:'100%',width:`${pct}%`,background:color,borderRadius:99}} />
                    </div>
                  </div>
                );
              })}
              {personnel.filter(p=>p.status==='active').length===0&&(
                <p style={{color:'var(--gray-400)',fontStyle:'italic',fontSize:'0.82rem'}}>Aucun personnel actif</p>
              )}
            </div>
          </div>
        </div>
      </Card>

      <Card>
        <CardHeader title="Demandes d'absence" subtitle="Historique et gestion"
          action={
            <div style={{display:'flex',gap:8}}>
              <select value={filterStatut} onChange={e=>setFilterStatut(e.target.value)} style={{width:'auto'}}>
                <option value="">Tous statuts</option>
                <option value="pending">En attente</option>
                <option value="approved">Approuvés</option>
                <option value="rejected">Refusés</option>
              </select>
              <Btn variant="gold" onClick={()=>setModalOpen(true)}>➕ Nouvelle demande</Btn>
            </div>
          }/>
        {loading?<Spinner/>:<Table columns={columns} data={filtered} emptyMessage="Aucune demande"/>}
      </Card>

      <Modal open={modalOpen} onClose={()=>setModalOpen(false)} title="Nouvelle demande d'absence">
        <form onSubmit={handleCreate} style={{padding:24}}>
          <FormRow cols={1}>
            <FormField label="Personnel *">
              <select value={form.personnel_id} onChange={e=>setForm({...form,personnel_id:e.target.value})} required>
                <option value="">— Sélectionner —</option>
                {personnel.map(p=>{
                  const pris=congesParPersonnel[p.id]||0;
                  const reste=QUOTA-pris;
                  const tag=form.type==='Congé annuel'?(reste<=0?' ⛔':(reste<=5?` (${reste}j)`:'')):'';
                  return<option key={p.id} value={p.id}>{p.first_name} {p.last_name} — {p.poste}{tag}</option>;
                })}
              </select>
            </FormField>
          </FormRow>

          {form.personnel_id&&form.type==='Congé annuel'&&(
            <div style={{margin:'0 0 14px',background:resteSelectionne<=0?'#fef2f2':'#f0fdf4',border:`1px solid ${resteSelectionne<=0?'#fca5a5':'#86efac'}`,borderRadius:10,padding:'10px 14px'}}>
              <div style={{display:'flex',justifyContent:'space-between',marginBottom:6}}>
                <p style={{fontSize:'0.8rem',fontWeight:700,color:resteSelectionne<=0?'#dc2626':'#166534'}}>
                  {resteSelectionne<=0?'⛔ Quota épuisé':'📅 Quota de congés'}
                </p>
                <span style={{fontWeight:700,color:resteSelectionne<=0?'#dc2626':'#166534'}}>{Math.max(0,resteSelectionne)} / {QUOTA} j. restants</span>
              </div>
              <div style={{height:8,background:'#e5e7eb',borderRadius:99,overflow:'hidden'}}>
                <div style={{height:'100%',width:`${Math.min(100,((QUOTA-Math.max(0,resteSelectionne))/QUOTA)*100)}%`,background:resteSelectionne<=0?'#dc2626':resteSelectionne<=5?'#f59e0b':'#16a34a',borderRadius:99}} />
              </div>
              {joursForm>0&&<p style={{fontSize:'0.75rem',marginTop:6,color:quotaDepasse?'#dc2626':'#166534',fontWeight:600}}>
                {quotaDepasse?`❌ Cette demande (${joursForm} j.) dépasse le quota`:`✅ Cette demande utilise ${joursForm} j. — reste ${resteSelectionne-joursForm} j.`}
              </p>}
            </div>
          )}

          {form.personnel_id&&form.type==='Permission spéciale'&&(
            <div style={{margin:'0 0 14px',background:permissionAtteinte?'#fef2f2':restePermission<=3?'#fffbeb':'#f5f3ff',border:`1px solid ${permissionAtteinte?'#fca5a5':restePermission<=3?'#fcd34d':'#c4b5fd'}`,borderRadius:10,padding:'10px 14px'}}>
              <div style={{display:'flex',justifyContent:'space-between',marginBottom:6}}>
                <p style={{fontSize:'0.8rem',fontWeight:700,color:permissionAtteinte?'#dc2626':restePermission<=3?'#92400e':'#5b21b6'}}>
                  {permissionAtteinte?'⛔ Quota de permissions atteint':'🗓️ Suivi des permissions'}
                </p>
                <span style={{fontWeight:700,color:permissionAtteinte?'#dc2626':restePermission<=3?'#92400e':'#5b21b6'}}>{Math.max(0,restePermission)} / {QUOTA_PERMISSION} j. restants</span>
              </div>
              <div style={{height:8,background:'#e5e7eb',borderRadius:99,overflow:'hidden'}}>
                <div style={{height:'100%',width:`${Math.min(100,((QUOTA_PERMISSION-Math.max(0,restePermission))/QUOTA_PERMISSION)*100)}%`,background:permissionAtteinte?'#dc2626':restePermission<=3?'#f59e0b':'#7c3aed',borderRadius:99}} />
              </div>
              <div style={{display:'flex',justifyContent:'space-between',marginTop:6,fontSize:'0.7rem',color:'var(--gray-500)'}}>
                <span>Autorisés : <strong>{QUOTA_PERMISSION} j.</strong></span>
                <span>Utilisés : <strong>{QUOTA_PERMISSION-Math.max(0,restePermission)} j.</strong></span>
                <span>Restants : <strong style={{color:permissionAtteinte?'#dc2626':restePermission<=3?'#f59e0b':'#7c3aed'}}>{Math.max(0,restePermission)} j.</strong></span>
              </div>
              {joursForm>0&&!permissionAtteinte&&<p style={{fontSize:'0.75rem',marginTop:6,color:quotaPermissionDepasse?'#dc2626':'#5b21b6',fontWeight:600}}>
                {quotaPermissionDepasse?`❌ Cette demande (${joursForm} j.) dépasse le quota`:`✅ Cette demande utilise ${joursForm} j. — reste ${restePermission-joursForm} j.`}
              </p>}
              {permissionAtteinte&&<p style={{fontSize:'0.75rem',marginTop:6,color:'#dc2626',fontWeight:600}}>❌ Cet employé a atteint sa limite annuelle de permissions spéciales.</p>}
            </div>
          )}

          <FormRow cols={1}>
            <FormField label="Type">
              <select value={form.type} onChange={e=>setForm({...form,type:e.target.value})}>
                {TYPES.map(t=>{
                  const isPermission=t==='Permission spéciale';
                  const bloque=isPermission&&form.personnel_id&&(permissionsParPersonnel[form.personnel_id]||0)>=QUOTA_PERMISSION;
                  return<option key={t} value={t} disabled={bloque}>{t}{bloque?' (quota atteint)':''}</option>;
                })}
              </select>
            </FormField>
          </FormRow>
          <FormRow>
            <FormField label="Début *"><input type="date" value={form.start_date} min={TODAY} onChange={e=>setForm({...form,start_date:e.target.value,end_date:''})} required /></FormField>
            <FormField label="Fin *"><input type="date" value={form.end_date} min={form.start_date||TODAY} onChange={e=>setForm({...form,end_date:e.target.value})} required /></FormField>
          </FormRow>
          {joursForm>0&&<div style={{background:'var(--gray-50)',border:'1px solid var(--gray-200)',borderRadius:8,padding:'8px 14px',marginBottom:14}}>
            <span style={{fontSize:'0.8rem',color:'var(--gray-500)'}}>Durée calculée : </span>
            <span style={{fontWeight:700,color:'var(--navy)'}}>{joursForm} jour(s) ouvrable(s)</span>
          </div>}
          <FormRow cols={1}><FormField label="Motif"><textarea value={form.reason} onChange={e=>setForm({...form,reason:e.target.value})} rows={3} style={{resize:'vertical'}}/></FormField></FormRow>
          <div style={{display:'flex',gap:10,justifyContent:'flex-end'}}>
            <Btn type="button" variant="secondary" onClick={()=>setModalOpen(false)}>Annuler</Btn>
            <Btn type="submit" variant="gold" disabled={saving||quotaDepasse||quotaPermissionDepasse||permissionAtteinte}>
              {saving?'Envoi...':(quotaDepasse||quotaPermissionDepasse||permissionAtteinte)?'⛔ Quota insuffisant':'📤 Soumettre'}
            </Btn>
          </div>
        </form>
      </Modal>
    </div>
  );
}
